import { useCallback, useMemo } from 'react';

import { add, format, isValid, sub } from 'date-fns';

import { useLocale } from 'state/intl';
import { isDateTimeLessThanOtherDateTime } from 'utils/dateTime';

import { ColorState, DateStatus, IStoreStatus, StoreStatus } from '../types';

export const useStoreStatus = ({
  openingHours,
  closingHours,
  dayWeek,
  yesterdayClosingHours,
  yesterdayOpeningHours,
  dateNow,
}: IStoreStatus) => {
  const { formatDate } = useLocale();

  const parseStoreHourAsDate = useCallback(
    (hours?: string, yesterday = false): Date | undefined => {
      if (!hours) {
        return;
      }

      let date = new Date(dateNow);

      if (yesterday) {
        date.setDate(date.getDate() - 1);
      }

      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const dateTimeString = `${year}/${month}/${day} ${hours}`;

      return new Date(dateTimeString);
    },
    [dateNow]
  );

  // for coverage of every scenario, we need this rules
  /**
   * The store availability is a quite tricky subject.
   * Our Sanity setup currently only supports a single open/close time per day,
   * so we need to apply some logic to handle shifts that last until the next day.
   *
   * The store is considered open if:
   *  1- it's not open today, but it's still open from yesterday shift:
   *     E.g:
   *      Monday open hours: 12:00 Monday Close Hours 00:30
   *      Tuesday open hours - null Tuesday Close Hours - null
   *      Current Time - Tuesday 00:20
   *      The store is considered open for Monday with the open status on Monday
   * 2 - it's within open hours of today:
   *     E.g:
   *      Monday Open Hours 12:00 Monday Close Hours: 00:30
   *      Current Time: Monday 17:00
   *      The store is considered open for Monday with the open status on Monday
   *
   * Other relevant scenarios:
   * 1 -
   *      Monday open hours: 12:00 Monday Close Hours 00:30
   *      Tuesday open hours - 03:00 Tuesday Close Hours - 10:00
   *      Current Time - Tuesday 00:20
   *    -> Should appear as open for Monday with the Open status on Monday
   * 2 -
   *      Monday open hours: 12:00 Monday Close Hours 00:30
   *      Tuesday open hours - 03:00 Tuesday Close Hours - 10:00
   *      Current Time - Tuesday 03:30
   *    -> Should appear as open for Tuesday with the Open Status on Tuesday
   */
  const isStillOpen = useCallback(() => {
    if (!openingHours && !closingHours) {
      if (
        yesterdayClosingHours &&
        yesterdayOpeningHours &&
        yesterdayClosingHours < yesterdayOpeningHours
      ) {
        /*
         * If yesterday closing hours are smaller than yesterday opening hours
         * then this means that the shift will finish in the next day
         */
        return isDateTimeLessThanOtherDateTime(
          dateNow,
          parseStoreHourAsDate(yesterdayClosingHours)
        );
      }

      return isDateTimeLessThanOtherDateTime(
        dateNow,
        parseStoreHourAsDate(yesterdayClosingHours, true)
      );
    }

    return (
      isDateTimeLessThanOtherDateTime(dateNow, parseStoreHourAsDate(openingHours)) &&
      isDateTimeLessThanOtherDateTime(dateNow, parseStoreHourAsDate(yesterdayClosingHours)) &&
      isDateTimeLessThanOtherDateTime(
        parseStoreHourAsDate(yesterdayClosingHours),
        parseStoreHourAsDate(openingHours)
      )
    );
  }, [
    closingHours,
    dateNow,
    openingHours,
    parseStoreHourAsDate,
    yesterdayClosingHours,
    yesterdayOpeningHours,
  ]);

  const closingHourRaw = useMemo(
    () => (isStillOpen() ? yesterdayClosingHours : closingHours),

    [closingHours, isStillOpen, yesterdayClosingHours]
  );
  const openingHourRaw = useMemo(
    () => (isStillOpen() ? yesterdayOpeningHours : openingHours),

    [openingHours, isStillOpen, yesterdayOpeningHours]
  );

  const closingDate = parseStoreHourAsDate(closingHourRaw);
  const openingDate = parseStoreHourAsDate(openingHourRaw);

  const isCloseStore = useCallback(
    (open: Date, close: Date) => dateNow <= open || dateNow >= close,
    [dateNow]
  );

  const isCloseAtStore = useCallback(
    (close: Date) => dateNow <= close && add(dateNow, { minutes: 60 }) >= close,
    [dateNow]
  );

  const isOpenStore = useCallback(
    (open: Date, close: Date) => dateNow >= open && dateNow < close,
    [dateNow]
  );

  const isOpenAtStore = useCallback(
    (open: Date) => dateNow <= open && add(dateNow, { minutes: 60 }) >= open,
    [dateNow]
  );

  const getNewCloseDate = useCallback(
    (open: Date, close: Date) => (close < open && !isStillOpen() ? add(close, { days: 1 }) : close),
    [isStillOpen]
  );

  const getNewOpenDate = useCallback(
    (open: Date, close: Date) => (close < open && isStillOpen() ? sub(open, { days: 1 }) : open),
    [isStillOpen]
  );

  const getTimeTableInfo = useCallback(() => {
    const isClosedToday = !openingDate && !closingDate;

    if (isClosedToday) {
      return { openDate: openingDate, closeDate: closingDate, status: StoreStatus.ClosedToday };
    }

    if (!openingDate || !closingDate || !isValid(openingDate) || !isValid(closingDate)) {
      return { openDate: undefined, closeDate: undefined, status: undefined };
    }

    return { openDate: openingDate, closeDate: closingDate, status: undefined };
  }, [closingDate, openingDate]);

  const getStoreStatusText = useCallback(
    (open: Date, close: Date) => {
      if (isCloseAtStore(close)) {
        return StoreStatus.ClosesAt;
      }
      if (isOpenAtStore(open)) {
        return StoreStatus.OpensAt;
      }
      if (isOpenStore(open, close)) {
        return StoreStatus.OpenNow;
      }
      if (isCloseStore(open, close)) {
        return StoreStatus.ClosedNow;
      }

      return undefined;
    },
    [isCloseAtStore, isCloseStore, isOpenAtStore, isOpenStore]
  );

  const getStoreStatus = useCallback(() => {
    const { status, openDate, closeDate } = getTimeTableInfo();
    if (status) {
      return status;
    }

    if (!openDate || !closeDate) {
      return undefined;
    }

    const newOpenDate = getNewOpenDate(openDate, closeDate);
    const newCloseDate = getNewCloseDate(openDate, closeDate);

    return getStoreStatusText(newOpenDate, newCloseDate);
  }, [getTimeTableInfo, getNewCloseDate, getNewOpenDate, getStoreStatusText]);

  const timeFormat = useCallback((date: DateStatus) => {
    const minutes = date?.getMinutes();
    return minutes === 0 ? 'hh aaa' : 'hh:mm aaa';
  }, []);

  const formatMessageWithTime = useCallback(
    (storeStatus: StoreStatus | undefined, message: string, openDateTime: DateStatus): string => {
      switch (storeStatus) {
        case StoreStatus.ClosesAt:
          return message && closingDate
            ? message.replace('{time}', formatDate(closingDate, timeFormat(closingDate)))
            : '';
        case StoreStatus.OpensAt:
          return message && openDateTime
            ? message.replace('{time}', formatDate(openDateTime, timeFormat(openDateTime)))
            : '';
        default:
          return message;
      }
    },
    [closingDate, formatDate, timeFormat]
  );

  const colorState = useMemo((): ColorState => {
    const storeStatus = getStoreStatus();

    switch (storeStatus) {
      case StoreStatus.ClosesAt:
      case StoreStatus.OpensAt:
        return ColorState.Upcoming;

      case StoreStatus.ClosedNow:
      case StoreStatus.ClosedToday:
        return ColorState.Closed;

      default:
        return ColorState.Open;
    }
  }, [getStoreStatus]);

  const hasInValidDayWeek = isStillOpen()
    ? false
    : dayWeek && dayWeek.toLowerCase() !== format(dateNow, 'iiii').toLowerCase();

  return {
    parseStoreHourAsDate,
    getStoreStatus,
    formatMessageWithTime,
    colorState,
    hasInValidDayWeek,
    closingHourRaw,
  };
};
